import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import React from "react";

import { Button, LoadingIndicator } from "../../components";
import CoachingMarkdown from "./CoachingMarkdown";
import { TaskRunResult } from "./types";
import { getMarkdown } from "./utils";

type CoachingPreviewModalProps = {
  taskRunResult: TaskRunResult;
  isOpen: boolean;
  onClose: () => void;
};

const CoachingPreviewModal: React.FC<CoachingPreviewModalProps> = ({
  taskRunResult,
  isOpen,
  onClose,
}) => {
  const { loading, error, taskRun } = taskRunResult;
  const statusMessage = taskRun?.statusMessage?.trim() || null;

  const showLoader =
    loading ||
    (taskRun && (!taskRun.status || taskRun.status === "IN_PROGRESS"));

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Preview coaching tips</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box w="100%">
            {showLoader && (
              <Flex flexDir="column" alignItems="center">
                <LoadingIndicator my="4" />
                <Text color="gray.500" fontSize="17px" fontWeight="500">
                  {statusMessage || "Generating preview..."}
                </Text>
                {statusMessage && (
                  <Alert status="info" mt="4" w="100%">
                    <AlertIcon />
                    <AlertDescription whiteSpace="pre-wrap">
                      {statusMessage}
                    </AlertDescription>
                  </Alert>
                )}
              </Flex>
            )}

            {!loading && error && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>
                  An error occurred while fetching the data.
                </AlertDescription>
              </Alert>
            )}

            {!loading && !error && taskRun && (
              <TaskRunOutput
                status={taskRun.status || null}
                output={taskRun.output}
                statusMessage={statusMessage}
              />
            )}

            {!loading && !error && !taskRun && (
              <Alert status="info">
                <AlertIcon />
                <AlertDescription>
                  Waiting for preview result. If this persists, run preview
                  again.
                </AlertDescription>
              </Alert>
            )}
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>Done</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TaskRunOutput: React.FC<{
  status: string | null;
  output: string;
  statusMessage: string | null;
}> = ({ status, output, statusMessage }) => {
  if (!status) {
    return null;
  }

  if (["COMPLETED", "COMPLETED_PREVIEW"].includes(status)) {
    const markdown = getMarkdown({ id: "", output, status });
    if (markdown) {
      return <CoachingMarkdown markdown={markdown} w="100%" />;
    }

    return (
      <Alert status="warning">
        <AlertIcon />
        <AlertDescription>
          {statusMessage ||
            "Preview completed, but no coaching content was generated."}
        </AlertDescription>
      </Alert>
    );
  }

  if (status === "FAILED") {
    return (
      <Alert status="warning" flexDir="column" alignItems="flex-start">
        <AlertIcon />
        <AlertDescription>
          There was a problem creating coaching tips for this user.
        </AlertDescription>
        <AlertDescription mt="2" whiteSpace="pre-wrap">
          {statusMessage || "This user may not have recent interviews."}
        </AlertDescription>
      </Alert>
    );
  }

  if (statusMessage) {
    return (
      <Alert status="info">
        <AlertIcon />
        <AlertDescription whiteSpace="pre-wrap">
          {statusMessage}
        </AlertDescription>
      </Alert>
    );
  }

  return null;
};

export default CoachingPreviewModal;
